// based on https://www.radix-ui.com/primitives/docs/components/dialog#api-reference

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import VisuallyHidden from '../../VisuallyHidden';
import styles from './baseDialog.module.scss';
import { Close } from '../../../icons';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={`${styles.dialogOverlay} ${className}`}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    dismissable?: boolean;
    showOverlay?: boolean;
    portalContainer?: HTMLElement | null;
  }
>(
  (
    {
      className,
      children,
      portalContainer,
      dismissable = true,
      showOverlay = true,
      ...props
    },
    ref
  ) => (
    <DialogPortal container={portalContainer}>
      {showOverlay && <DialogOverlay />}
      <DialogPrimitive.Content
        ref={ref}
        className={`${styles.dialogContent} ${className}`}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className={styles.dialogClose}>
          {dismissable && <Close />}
          <VisuallyHidden>Close</VisuallyHidden>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.dialogHeader} ${className}`} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.dialogFooter} ${className}`} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogDetails = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.dialogDetails} ${className}`} {...props} />
);
DialogDetails.displayName = 'DialogDetails';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={`${styles.dialogTitle} ${className}`}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={`${styles.dialogDescription} ${className}`}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogDetails
};
